import { useContext, useEffect, useMemo, useRef, useState } from 'react'

import { theme, useHealthToolsCache } from '@hhgtech/hhg-components'
import { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import {
  LOCALE_HAS_COMMUNITY_SITE,
  QUERY_HEALTH_TOOL,
} from '@hhgtech/hhg-components/constants'
import { Slider, Text } from '@hhgtech/hhg-components/mantine'
import { Flex, Stack, rem } from '@mantine/core'
import { useForm } from '@mantine/form'
import dayjs from 'dayjs'
import isEmpty from 'lodash/isEmpty'
import { useIntl } from 'react-intl'

import { getHelloSitesUrl } from 'api/utils/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import AdditionalQuestionGroup, {
  HandlerProps as AdditionalQuestionHandlerProps,
} from 'components/atoms/healthTools/form/additionalQuestionGroup'
import { AgeInput } from 'components/atoms/healthTools/form/ageInput'
import { CheckYourSelfSwitching } from 'components/atoms/healthTools/form/checkYourSelfSwitching'
import { AdBlock } from 'components/molecules/adBlock'
import { FixedBottomAdBlock } from 'components/molecules/adBlock/deferFixedBottomAdBlock'
import { BmiFormHelpers } from 'components/molecules/bmiForm'
import { FaqAccordion } from 'components/molecules/faqAccordion'
import { HealthToolBreadcrumb } from 'components/molecules/healthToolBreadcrumb'
import { PregnancyNavigation } from 'components/molecules/pregnancyNavigation'
import { PWGInputForm } from 'components/molecules/pwgInputForm'
import { ToolsBannerCardV2 } from 'components/molecules/toolsBannerCardV2'
import { ToolsSubmitButton } from 'components/molecules/toolsSubmitButton'
import { StyledFormControl } from 'components/organisms/babyGrowthForm/index.styled'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import { StyledHealthTools } from 'components/pages/healthTools/index.styled'
import { HealthToolLayout } from 'components/templates/healthToolLayout'
import { NavigatorLayout } from 'components/templates/navigatorLayout'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { DISPLAY_DATE_FORMAT, assetImagePath } from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import {
  FooterInfo,
  PregWeightInfo,
  PregnancyWeightLocalStoredType,
  PwgInfo,
} from 'interfaces/types'
import { AppContext } from 'state/appContext'
// import { getAdTag, getCountryAdTag, getLocaleCategoryAdTag } from 'utils/ads'
import { getAdTag, getCountryAdTag, getLocaleCategoryAdTag } from 'utils/ads'
import { checkAndSaveUserInfo } from 'utils/healthTools'

import { ToolPageType } from '.'
import {
  FormControl,
  StyledContent,
  StyledRightColumnContainer,
  StyledSubmitButton,
} from './index.styled'

export type InputError = {
  weightBefore: string
  weight: string
  height: string
}

export type Props = {
  type: ToolPageType
  isMobileSsr: boolean
  pwgContent: PwgInfo
  pageType?: string
  userInfo?: any
  footerInfo?: FooterInfo
  dataNavMenuContent?: NavMenuContentType
}

const PWGToolPage = ({ pwgContent, footerInfo, dataNavMenuContent }: Props) => {
  const { formatMessage: f } = useIntl()

  const isMobile = useIsMobile()

  const {
    state: { isLoggedIn, userInfo },
  } = useContext(AppContext) || {}

  const { locale, router, asPath } = useRouterExtend()

  const { getHealthToolLink } = useHealthToolsCache(locale)
  const form = useForm<any>({
    initialValues: {
      isFirstPregnant: true,
    },
  })
  const additionQuestionRef = useRef<AdditionalQuestionHandlerProps>(null)
  const [cycleValue, setCycleValue] = useState(28)

  const [info, setInfo] = useState<PregWeightInfo>({
    yourSelf: 'self',
    week: '1',
    weightBefore: '',
    height: '',
    weight: '',
    twins: 'notwins',
  } as PregWeightInfo)

  const [inputErrorMessage, setInputErrorMessage] = useState<InputError>(
    {} as InputError,
  )

  const [isYourSelf, setIsYourSelf] = useState('self')
  const [isTwins, setIsTwins] = useState(false)

  const onFormChange = (i: PregWeightInfo) => {
    setInfo(i)
  }

  const validateInputs = () => {
    const _input = {} as InputError
    const newErrors: any = {}
    if (!parseFloat(info.weightBefore) || parseFloat(info.weightBefore) > 200) {
      newErrors.weightBefore = f({
        id: 'pregnancyWeightGain.weightInput.error',
        defaultMessage: 'Please enter a valid weight',
      })
    }
    if (
      !parseFloat(info.height) ||
      parseFloat(info.height) <= 0 ||
      parseFloat(info.height) > 300
    ) {
      newErrors.height = f({
        id: 'pregnancyWeightGain.heightInput.error',
        defaultMessage: 'Please enter a valid height',
      })
    }
    if (
      !parseFloat(info.weight) ||
      parseFloat(info.weight) > 200 ||
      (cycleValue >= 4 * 3 &&
        parseFloat(info.weight) <= parseFloat(info.weightBefore))
    ) {
      newErrors.weight = f({
        id: 'pregnancyWeightGain.weightInput.error',
        defaultMessage: 'Please enter a valid weight',
      })
    }

    if (isEmpty(newErrors)) {
      return true
    } else {
      setInputErrorMessage({
        ..._input,
        ...newErrors,
      })
      return false
    }
  }
  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: getLocaleCategoryAdTag(locale, asPath, ['health-tools/pwg']),
  })
  const enabledAd = !!pwgContent?.enable_ad

  const onSubmit = () => {
    additionQuestionRef?.current?.validate(() => {
      if (validateInputs()) {
        const bmi =
          Math.round(
            (parseFloat(info.weight) /
              (((parseFloat(info.height) / 100) * parseFloat(info.height)) /
                100)) *
              10,
          ) / 10
        const weightGain =
          parseFloat(info.weight) - parseFloat(info.weightBefore)
        const type = isTwins ? 'twins' : 'notwins'

        const currentOrigin = window.location.origin
        const currentPathname = window.location.pathname
        const pwgInfo: PregnancyWeightLocalStoredType = {
          yourSelf: isYourSelf,
          pwg: weightGain,
          bmi: bmi,
          week: cycleValue,
          twin: isTwins,
          weightBefore: parseFloat(info.weightBefore),
          weight: parseFloat(info.weight),
          height: parseFloat(info.height),
          isFirstPregnant: Number(
            Boolean(form.getInputProps('isFirstPregnant').value),
          ),
          additionalQuestions: form.getInputProps('additionalQuestions').value,
          ...(form.getInputProps('birthday').value && {
            birthday: dayjs(form.getInputProps('birthday').value).format(
              DISPLAY_DATE_FORMAT,
            ),
          }),
          source_url: window.location.href,
        }
        localStorage.setItem(
          'pwgInfo',
          JSON.stringify({
            ...pwgInfo,
            ...(pwgInfo.birthday && {
              birthdayDate: form.getInputProps('birthday').value,
            }),
          }),
        )
        ;(async () => {
          const destination =
            currentOrigin +
            currentPathname +
            `?result=1&${QUERY_HEALTH_TOOL.pwg}=` +
            weightGain +
            `&${QUERY_HEALTH_TOOL.bmi}=` +
            bmi +
            `&${QUERY_HEALTH_TOOL.week}=` +
            cycleValue +
            `&${QUERY_HEALTH_TOOL.twins}=` +
            type +
            `&${QUERY_HEALTH_TOOL.weightBefore}=` +
            info.weightBefore +
            `&${QUERY_HEALTH_TOOL.weight}=` +
            info.weight +
            (LOCALE_HAS_COMMUNITY_SITE.includes(locale)
              ? '&joincommunity=1'
              : '')

          if (!isLoggedIn) {
            BmiFormHelpers.syncParamsToUseOnSSOForm(
              {
                gender: isYourSelf === 'self' ? 'female' : '',
                birthday:
                  isYourSelf === 'self'
                    ? form.getInputProps('birthday').value
                    : undefined,
              },
              router,
            )
            redirectToSSOWithReturnUrl(destination, 'pregnancy-weight-gain')
          } else {
            if (userInfo?.id && form.getInputProps('birthday').value) {
              await checkAndSaveUserInfo({
                userInfo,
                data: {
                  birthday: form.getInputProps('birthday').value,
                },
              })
            }
            router.push(destination)
          }
        })()
      }
    })
  }

  const pwgInfo = useMemo(
    () => ({
      title: f({
        id: 'tools.info',
        defaultMessage: 'Info',
      }),
      items: pwgContent.info,
    }),
    [pwgContent.info],
  )

  const [
    isOpenPregnancyNavigationDrawer,
    setIsOpenPregnancyNavigationDrawer,
  ] = useState(false)

  const pageItem = [
    {
      label: f({ id: 'healthtools.title' }),
      value: `/health-tools`,
    },
    {
      label: f({
        id: 'pregnancyWeightGain.heading',
        defaultMessage: 'Pregnancy Weight Gain',
      }),
      value: '#',
      dataEventCategory: 'Breadcrumb',
    },
  ]

  useEffect(() => {
    userInfo?.id &&
      userInfo?.birthday &&
      form.setFieldValue('birthday', dayjs(userInfo?.birthday).toDate())
  }, [userInfo?.id])

  return (
    <NavigatorLayout
      title={f({
        id: 'pregnancyWeightGain.seoTitle',
        defaultMessage: 'Pregnancy Weight Gain',
      })}
      description={f({
        id: 'pregnancyWeightGain.seoDescription',
        defaultMessage: 'Pregnancy Weight Gain',
      })}
      image={`${getHelloSitesUrl(locale)}/thumbnail-pwg.png?v=1`}
      SEOData={{
        schemaData: f({
          id: 'pregnancyWeightGain.seoSchema',
          defaultMessage: 'Pregnancy Weight Gain',
        }),
        apiMetaData: pwgContent?.metaTag,
        isIndexable: true,
      }}
      footerInfo={footerInfo}
      dataNavMenuContent={dataNavMenuContent}
      activeMenuService="tools"
    >
      <StyledHealthTools>
        <ToolsBannerCardV2
          breadcrumbProps={{ pageItem }}
          icon={
            <img
              alt="health-tool-icon"
              loading="lazy"
              src={pwgContent.featuredImage || `${assetImagePath}/pwg-icon.svg`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          }
          heading={
            pwgContent?.title ||
            f({
              id: 'pregnancyWeightGain.heading',
              defaultMessage: 'Pregnancy Weight Gain',
            })
          }
          description={
            pwgContent?.description ||
            f({
              id: 'pregnancyWeightGain.newDescription',
              defaultMessage:
                'This calculator can be used for women who want to see what their healthy weight gain range during pregnancy is based upon what their weight was before they fell pregnant.',
            })
          }
          reviewBy={pwgContent?.reviewBy}
          isMobile={isMobile}
          isPink
        />
        <HealthToolLayout>
          <HealthToolLayout.LeftColumn>
            <StyledContent>
              <FormControl>
                <Flex
                  gap={rem(24)}
                  sx={(theme) => ({
                    [theme.fn.smallerThan('sm')]: {
                      flexDirection: 'column',
                    },
                    [theme.fn.largerThan('sm')]: {
                      '& > div': {
                        width: '100%',
                        flexGrow: 1,
                      },
                    },
                  })}
                >
                  <StyledFormControl>
                    <AgeInput {...form.getInputProps('birthday')} />
                  </StyledFormControl>
                  <StyledFormControl>
                    <CheckYourSelfSwitching
                      checked={isYourSelf === 'self'}
                      onChange={(e) =>
                        setIsYourSelf(
                          e.currentTarget.checked ? 'self' : 'other',
                        )
                      }
                    />
                  </StyledFormControl>
                </Flex>
              </FormControl>
              <FormControl>
                <Text as="label" size="s4" color={theme.colors.gray600}>
                  {f({
                    id: 'pregnancyWeightGain.weekInput',
                    defaultMessage: 'How many weeks pregnant are you?',
                  })}
                </Text>
                <Slider
                  withControl
                  min={1}
                  max={40}
                  step={1}
                  value={cycleValue}
                  onChange={setCycleValue}
                />
              </FormControl>

              <PWGInputForm
                info={info}
                errorMessage={inputErrorMessage}
                onChange={onFormChange}
              />
              <Stack spacing={rem(24)}>
                <CheckYourSelfSwitching
                  checked={isTwins}
                  label={f({
                    id: 'pregnancyWeightGain.havingTwins',
                    defaultMessage: 'Are you having twins?',
                  })}
                  onChange={(event) => setIsTwins(event.currentTarget.checked)}
                />
                <CheckYourSelfSwitching
                  {...form.getInputProps('isFirstPregnant')}
                  defaultChecked
                  label={f({
                    id: 'tools.isFirstPregnant',
                    defaultMessage: 'Is this the first pregnant?',
                  })}
                  onChange={(v) =>
                    form.setFieldValue(
                      'isFirstPregnant',
                      v.currentTarget.checked,
                    )
                  }
                />
                <AdditionalQuestionGroup
                  ref={additionQuestionRef}
                  additionalQuestions={pwgContent.listQuestions}
                  onChange={(v) => form.setFieldValue('additionalQuestions', v)}
                />
                <StyledSubmitButton>
                  <ToolsSubmitButton
                    onClick={onSubmit}
                    source="Health Tool"
                    toolName="Pregnant Weight"
                    title={f({
                      id: 'bmi.submit',
                      defaultMessage: 'Calculate',
                    })}
                    link={getHealthToolLink(HEALTH_TOOL_TYPE.PWG)}
                  />
                </StyledSubmitButton>
              </Stack>

              {enabledAd && isMobile && (
                <AdBlock
                  adUnitPath={adUnitPath}
                  className="top-ad"
                  size={[300, 250]}
                  divId={'div-gpt-ad-topbanner-mobile'}
                  pos={['TopBanner']}
                />
              )}
              {pwgInfo && pwgInfo.items && pwgInfo.items.length > 0 && (
                <>
                  <FaqAccordion
                    // title={pwgInfo.title}
                    faqs={pwgInfo.items}
                    titleProps={{
                      icon: (
                        <img
                          style={{
                            height: '16px',
                            width: '16px',
                          }}
                          src={`${assetImagePath}/info-icon.svg`}
                          alt=""
                          loading="lazy"
                        />
                      ),
                      headingTitle: pwgInfo.title,
                    }}
                  />
                  {isMobile && <div className="top-ad"></div>}
                </>
              )}
            </StyledContent>
          </HealthToolLayout.LeftColumn>
          <HealthToolLayout.RightColumn>
            <>
              {!isMobile && (
                <StyledRightColumnContainer>
                  {enabledAd && (
                    <div className="top-ad">
                      <AdBlock
                        adUnitPath={adUnitPath}
                        size={[
                          [300, 250],
                          [300, 600],
                        ]}
                        divId={'div-gpt-ad-topbanner-desktop'}
                        pos={['TopBanner']}
                      />
                    </div>
                  )}
                  <PregnancyNavigation
                    categories={pwgContent?.pregnancyNavigation}
                  />

                  <div className="bottom-ad"></div>
                </StyledRightColumnContainer>
              )}
            </>
          </HealthToolLayout.RightColumn>
        </HealthToolLayout>
        {enabledAd && (
          <FixedBottomAdBlock
            adUnitPath={adUnitPath}
            className="article-bottom-ad"
            isMobile={isMobile}
          />
        )}
        {/* </Container> */}
      </StyledHealthTools>
      <MobileBottomNavigationWrapper.FitnessHealthTool appTool="baby-kick-counter" />
      {isMobile && (
        <>
          <PregnancyNavigation
            categories={pwgContent?.pregnancyNavigation}
            drawerProps={{
              open: isOpenPregnancyNavigationDrawer,
              onClose: () => setIsOpenPregnancyNavigationDrawer(false),
            }}
          />

          <HealthToolBreadcrumb pageItem={pageItem} />
        </>
      )}
    </NavigatorLayout>
  )
}

export default PWGToolPage
